import acerto from '@acerto/web-sdk';
import { datadogRum } from '@datadog/browser-rum';
import { detectarDisplayMode } from '@shared/pwa/utils/displayMode';
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import ApiErrorCode from '@/models/enums/ApiErrorCode';
import HttpStatusCode from '@/models/enums/HttpStatusCode';
import ApiError from '@/models/resources/ApiError';
import { ApiResponse, isApiResponse, isApiResponseInvalid } from '@/models/resources/ApiResponse';
import { getRouter } from '@/router/indexRouter';

import serverTimeService from '@/services/ServerTimeService';

import storageService from './StorageService';

function updateServerTime(date: string): void {
  try {
    serverTimeService.updateDiff(date);
  } catch (e) {
    logger.warn('Erro ao tentar sincronizar relógio com o servidor:', e);
    logger.tracker.error(
      'Erro ao tentar sincronizar relógio com o servidor',
      { originalError: e },
    );
  }

  const { timeDiff } = serverTimeService;
  storageService.serverTime.set({ savedAt: Date.now(), timeDiff });
}

function replaceMultiCredorUrl(url: string) {
  return url.replace(/\/api\/mc-([a-zA-Z0-9-]+)\//, '/api/credores/$1/');
}

function getEnvironmentBaseURL(): string {
  if (window.location.hostname === 'localhost') {
    return '';
  }

  return appSettings.VUE_APP_API_PORTAL_BASE_URL;
}

export function requestInterceptor(requestConfig: InternalAxiosRequestConfig) {
  const token = storageService.session.get();
  let { baseURL, url } = requestConfig;
  const isPortalRequest = url?.toLowerCase().startsWith('/api/portal/');

  if (url) {
    /**
     * As chamadas para "/api/portal/[...]" devem
     * apontar para uma baseURL específica, independente da origem.
     *
     * Mais informações em:
     * https://acerto.atlassian.net/browse/TES-15872
     */
    if (isPortalRequest) {
      baseURL = getEnvironmentBaseURL();
    }

    /**
     * As chamadas para multicredor, com "/api/mc-[api]/[...]", devem
     * ser substituídas por "/api/credores/[api]/[...]".
     *
     * Mais informações em:
     * https://acerto.atlassian.net/browse/TES-15965
     */
    url = replaceMultiCredorUrl(url);
  }

  const fallbackToken = isPortalRequest ? null : storageService.sessionSS.get();
  const rawToken = token?.rawToken ?? fallbackToken?.rawToken;

  if (rawToken) {
    requestConfig.headers.set('Authorization', `bearer ${rawToken}`);
  }
  requestConfig.headers.set('Cache-Control', 'no-store');
  requestConfig.headers.set('pragma', 'no-cache');

  return {
    ...requestConfig,
    baseURL,
    url,
  };
}

export function errorInterceptor({ response }: AxiosError): Promise<never> {
  if (!response) return Promise.reject(new Error('Request failed without a response'));
  // Verifica se é erro relacionado ao Google Login (não bloqueante)
  const isGoogleLoginError = response.config.url?.includes('/api/portal/Google/Login')
    && response.status === HttpStatusCode.BAD_REQUEST;

  if (isGoogleLoginError) {
    // Erros do Google Login não são bloqueantes - apenas loga como warning
    logger.warn('Erro no Google Login (não bloqueante):', {
      status: response.status,
      url: response.config.url,
      data: response.data,
    });

    // Retorna uma promise rejeitada, mas sem bloquear o fluxo
    // O componente que chamou deve tratar esse erro adequadamente
    return Promise.reject(new Error(JSON.stringify(response)));
  }

  datadogRum.addError('sys:http_service:error', {
    status: response.status,
    url: response.config.url,
    method: response.config.method,
    data: response.data,
  });

  acerto.sendEvent({
    categoria: 'log',
    nome: 'http_service_error',
    tipo: 'onerror',
    payload: {
      status: response.status,
      url: response.config.url,
      method: response.config.method,
      display_mode: detectarDisplayMode(),
    },
  });

  if (response.headers.date) updateServerTime(response.headers.date);

  const router = getRouter();

  const { data, status } = response;
  const requestUrl = response.config.url?.toLowerCase() ?? '';
  const isDeeplinkSimpleSignOnRequest = requestUrl.includes('/api/portal/simplesignon');

  // Fluxos de deeplink tratam 401 na camada de tela (ex.: link inválido)
  if (status === HttpStatusCode.UNAUTHORIZED && isDeeplinkSimpleSignOnRequest) {
    return Promise.reject(new Error(JSON.stringify(response)));
  }

  // O serviço de Web Push valida o JWT por conta própria, e a assinatura sai em segundo plano
  // (login, abertura do app): um 401 dele não pode encerrar a sessão do portal.
  if (status === HttpStatusCode.UNAUTHORIZED && requestUrl.startsWith('/api/push/')) {
    return Promise.reject(new Error(`HTTP error ${status} for ${requestUrl}`));
  }

  if (status === HttpStatusCode.UNAUTHORIZED) {
    if (router) {
      router.push('/logout');
    }
    throw new axios.CanceledError('401 - redirect para logout');
  }

  if (isApiResponseInvalid(data)) {
    const apiError = new ApiError(data);

    if (apiError.is(ApiErrorCode.SESSAO_EXPIRADA)) {
      if (router) {
        router.push('/logout');
      }
    } else {
      return Promise.reject(apiError);
    }
  }

  return Promise.reject(new Error(`HTTP error ${response.status} for ${response.config.url ?? 'unknown URL'}`));
}

function responseInterceptor<T = unknown>( response: AxiosResponse<T>): AxiosResponse<T> | never {
  if (response.headers.date) updateServerTime(response.headers.date);

  if(isApiResponse(response.data)){
    const payload = response.data as ApiResponse<T>;
    if (payload.isValid) {
      return response;
    }
  
    if (payload.errors) {
      const apiError = new ApiError(payload.errors);
  
      if (apiError.is(ApiErrorCode.SESSAO_EXPIRADA)) {
        const router = getRouter();
  
        if (router) {
          router.push('/logout');
        }
        // Lança erro para satisfazer o tipo 'never' e evitar processamento adicional
        throw apiError;
      }
  
      // TODO: implementar handlers para error.code NOTIFICACAO_USUARIO e ERRO_CREDOR
      throw apiError;
    }
    throw new Error('Resposta da API inválida');
  }

  return response;
}

export default class HttpService {
  public axios: AxiosInstance;

  constructor() {
    this.axios = axios.create();
    this.axios.interceptors.request.use(requestInterceptor);
    this.axios.interceptors.response.use(responseInterceptor, errorInterceptor);
  }

  protected async get<T>(...args: Parameters<typeof this.axios.get>): Promise<T> {
    return (await this.axios.get<T>(...args)).data;
  }

  protected async post<T>(...args: Parameters<typeof this.axios.post>): Promise<T> {
    return (await this.axios.post<T>(...args)).data;
  }

  protected async delete<T>(...args: Parameters<typeof this.axios.delete>): Promise<T> {
    return (await this.axios.delete<T>(...args)).data;
  }
}

export const httpService = new HttpService();
